import { z } from 'zod';
import { hexColorSchema, optionalText, trimmed } from './_fields.js';
import { dateOnlySchema } from './patients.js';

/**
 * Widget de réservation : la configuration côté cabinet et les trois appels publics.
 * Tout ce qui sort des routes publiques est une liste blanche explicite — la V1 renvoyait la
 * ligne de configuration entière, jeton compris.
 */

/* -------------------------------------------------------------------------- */
/* Configuration (propriétaire du cabinet)                                     */
/* -------------------------------------------------------------------------- */

/** Couleur principale par défaut de la page publique, celle de la V1. */
export const DEFAULT_BOOKING_PRIMARY_COLOR = '#0066cc';

/**
 * Couleur principale : `#rrggbb` strict, ramené en minuscules pour ne stocker qu'une forme — la
 * base le vérifie aussi (`0078`). Ni `#abc`, ni nom CSS, ni `rgb()` : la valeur finit dans une
 * variable CSS de la page publique, et seule cette forme est sûre à y placer.
 */
export const bookingPrimaryColorSchema = hexColorSchema.transform((value) => value.toLowerCase());

/** Bornes des réglages de la page publique, communes à l'enregistrement et à l'aperçu. */
const bookingSlotRulesShape = {
  /** Pas de la grille de créneaux proposés, en minutes. */
  slotDurationMinutes: z.int().min(5).max(120),
  /** Délai minimum entre maintenant et le début du créneau réservable. */
  minAdvanceHours: z.int().min(0).max(720),
  /** Horizon maximum de réservation, en jours. */
  maxAdvanceDays: z.int().min(1).max(365),
};

/**
 * Tout ce qui façonne la page publique. L'aperçu du cabinet l'envoie **entier**, tel qu'il est en
 * cours d'édition ; l'enregistrement en accepte n'importe quelle partie.
 */
export const bookingPageSettingsSchema = z
  .object({
    ...bookingSlotRulesShape,
    welcomeMessage: optionalText(1000),
    /** Types de consultation proposés au public. Vide = aucun, donc widget sans offre. */
    consultationTypeIds: z.array(z.uuid()).max(50),
    primaryColor: bookingPrimaryColorSchema,
    /** E-mail exigé du patient. Le serveur refuse la réservation qui l'omet. */
    requireEmail: z.boolean(),
    /** Téléphone exigé du patient. Les deux peuvent être décochés : le cabinet l'assume. */
    requirePhone: z.boolean(),
  })
  .strict();
export type BookingPageSettingsInput = z.infer<typeof bookingPageSettingsSchema>;

export const bookingWidgetConfigUpdateSchema = bookingPageSettingsSchema
  .partial()
  .extend({
    isEnabled: z.boolean().optional(),
    /** Invalide l'ancien lien public et en émet un nouveau. */
    regenerateToken: z.boolean().optional(),
  })
  .strict();
export type BookingWidgetConfigUpdateInput = z.infer<typeof bookingWidgetConfigUpdateSchema>;

export interface BookingWidgetConfigDto {
  organizationId: string;
  isEnabled: boolean;
  publicToken: string;
  slotDurationMinutes: number;
  minAdvanceHours: number;
  maxAdvanceDays: number;
  welcomeMessage: string | null;
  consultationTypeIds: string[];
  primaryColor: string;
  requireEmail: boolean;
  requirePhone: boolean;
  createdAt: string;
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Routes publiques                                                            */
/* -------------------------------------------------------------------------- */

/** Le jeton porte l'identifiant du cabinet et un secret : voir `booking-public.service.ts`. */
export const bookingTokenParamSchema = z.object({ token: z.string().min(20).max(120) }).strict();

export const bookingSlotsQuerySchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    practitionerMemberId: z.uuid(),
    consultationTypeId: z.uuid(),
  })
  .strict();
export type BookingSlotsQuery = z.infer<typeof bookingSlotsQuerySchema>;

/**
 * Créneaux de l'aperçu du cabinet : la même requête que la page publique, plus les règles en
 * cours d'édition. Un corps JSON plutôt qu'une chaîne de requête : des entiers typés, sans
 * conversion à écrire, et rien n'est modifié côté serveur.
 */
export const bookingPreviewSlotsSchema = bookingSlotsQuerySchema
  .extend(bookingSlotRulesShape)
  .strict();
export type BookingPreviewSlotsInput = z.infer<typeof bookingPreviewSlotsSchema>;

/** Adresse facultative : la chaîne vide devient `null`, une valeur présente doit être valide. */
const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.union([z.literal(''), z.email().max(254)]))
  .transform((value) => (value === '' ? null : value))
  .nullable();

/**
 * `birthDate` est exigée alors que la V1 ne la demandait pas : la réservation crée désormais un
 * vrai dossier patient temporaire, et un dossier de santé sans date de naissance n'en est pas un.
 *
 * E-mail et téléphone sont **facultatifs dans la forme** : c'est la configuration du cabinet
 * (`requireEmail`, `requirePhone`) qui les exige, et elle n'est connue qu'une fois le jeton
 * résolu. Le serveur la vérifie alors (`booking-create.service.ts`, 422 `contact_required`) ; le
 * formulaire public applique la même règle avant l'envoi (`bookingContactSchemaFor`).
 */
export const publicBookingCreateSchema = z
  .object({
    slotStartsAt: z.iso.datetime(),
    practitionerMemberId: z.uuid(),
    consultationTypeId: z.uuid(),
    firstName: trimmed(100),
    lastName: trimmed(100),
    birthDate: dateOnlySchema,
    email: optionalEmail.optional(),
    phone: optionalText(40).optional(),
    message: optionalText(1000).optional(),
  })
  .strict();
export type PublicBookingCreateInput = z.infer<typeof publicBookingCreateSchema>;

/** Exigences de contact d'un cabinet, telles que la page publique les reçoit. */
export interface BookingContactRequirements {
  requireEmail: boolean;
  requirePhone: boolean;
}

export type BookingContactField = 'email' | 'phone';

/**
 * Champs exigés mais laissés vides, dans l'ordre du formulaire. C'est la règle du serveur : il
 * refuse la réservation avec cette liste (`contact_required`).
 *
 * Les deux exigences décochées sont acceptées : le cabinet reçoit alors une demande sans moyen de
 * rappeler le patient, et c'est son choix — le réglage le dit à côté des interrupteurs.
 */
export function missingContactFields(
  input: { email?: string | null; phone?: string | null },
  requirements: BookingContactRequirements,
): BookingContactField[] {
  const missing: BookingContactField[] = [];
  if (requirements.requireEmail && !input.email?.trim()) missing.push('email');
  if (requirements.requirePhone && !input.phone?.trim()) missing.push('phone');
  return missing;
}

/**
 * Les deux champs de contact tels que le formulaire public les valide, selon les exigences du
 * cabinet : exigé, le champ refuse le vide ; facultatif, il accepte le vide et le ramène à `null`.
 * Même résultat que `missingContactFields`, mais champ par champ, pour que la phrase de refus
 * s'affiche sous le bon champ dès le premier envoi.
 */
export function bookingContactFieldsFor(requirements: BookingContactRequirements) {
  return {
    email: requirements.requireEmail
      ? z.string().trim().toLowerCase().pipe(z.email().max(254))
      : optionalEmail.optional(),
    phone: requirements.requirePhone ? trimmed(40) : optionalText(40).optional(),
  };
}

export interface PublicConsultationTypeDto {
  id: string;
  name: string;
  durationMinutes: number;
  price: number | null;
}

/** Prénom et couleur seulement : le public n'a pas à connaître la composition exacte du cabinet. */
export interface PublicPractitionerDto {
  memberId: string;
  firstName: string;
  agendaColor: string | null;
}

/**
 * Ce que la page publique affiche. Liste blanche : la couleur et les deux exigences servent au
 * rendu du formulaire, rien d'autre de la configuration ne sort (ni jeton, ni auteur, ni dates).
 */
export interface PublicBookingInfoDto {
  organizationName: string;
  welcomeMessage: string | null;
  primaryColor: string;
  requireEmail: boolean;
  requirePhone: boolean;
  slotDurationMinutes: number;
  minAdvanceHours: number;
  maxAdvanceDays: number;
  consultationTypes: PublicConsultationTypeDto[];
  practitioners: PublicPractitionerDto[];
}

export interface BookingSlotDto {
  startsAt: string;
  endsAt: string;
}

export interface PublicBookingResultDto {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
}
